/**
 * Parks & Rec yoga / gentle movement — Sep 2026 calendar.
 *
 * Power Yoga is listed because it is on the same calendar, but it is
 * NOT gentle (Baptiste flow). WellWay offers yoga among 40+ weekly
 * classes; we do not invent a named Gentle Yoga class there.
 *
 * Source: babcockranchliving.com Parks & Rec calendar, September 2026.
 */

import { RANCH_CALENDAR_URL, REC1_CATALOG_URL } from '@shared/ranch-feed';

export type MovementIntensity = 'gentle' | 'active' | 'studio';

export interface MovementSession {
  id: string;
  interestId: string;
  name: string;
  intensity: MovementIntensity;
  /** Short badge: "Closest to gentle", "Not gentle", "Confirm on the schedule". */
  intensityLabel: string;
  when: string;
  venue: string;
  address: string;
  summary: string;
  details: string[];
  href?: string;
  phone?: string;
  sourceNote: string;
}

export const LISA_SEEDED_MOVEMENT_IDS = [
  'yin-yoga',
  'yoga-in-the-park',
  'tai-chi',
  'sound-bath',
] as const;

export const YOGA_SESSIONS: MovementSession[] = [
  {
    id: 'yin-yoga',
    interestId: 'yin-yoga',
    name: 'Yin Yoga',
    intensity: 'gentle',
    intensityLabel: 'Closest to gentle',
    when: 'Wed 9/16 · 1:00–2:00 PM',
    venue: 'Cypress Lodge',
    address: '43511 Bluebird Trail, Punta Gorda, FL 33982',
    summary:
      'Passive, held poses for 3–5 minutes. The Parks & Rec offering closest to gentle movement.',
    details: [
      'Wednesday, September 16, 2026, 1:00–2:00 PM at Cypress Lodge.',
      'Long holds, not a flowing vinyasa — the softest named yoga class on this month’s calendar.',
    ],
    href: REC1_CATALOG_URL,
    sourceNote: 'Babcock Ranch Parks & Rec calendar, September 2026 (babcockranchliving.com).',
  },
  {
    id: 'yoga-in-the-park',
    interestId: 'yoga-in-the-park',
    name: 'Yoga in the Park',
    intensity: 'gentle',
    intensityLabel: 'Free · all bodies',
    when: 'Fri 9/25 · 9:00 AM',
    venue: 'Palmetto Park',
    address: '17850 Babcock Trl, Babcock Ranch, FL 33982',
    summary: 'Free outdoor class, published as accessible to all bodies.',
    details: [
      'Friday, September 25, 2026, 9:00 AM at Palmetto Park.',
      'No fee listed. Bring a mat if you have one; confirm weather on the town calendar.',
    ],
    href: RANCH_CALENDAR_URL,
    sourceNote: 'Babcock Ranch Parks & Rec calendar, September 2026 (babcockranchliving.com).',
  },
  {
    id: 'tai-chi',
    interestId: 'tai-chi',
    name: 'Tai Chi',
    intensity: 'gentle',
    intensityLabel: 'Soft alternative',
    when: 'Most weekdays · 10:30 AM',
    venue: 'Cypress Lodge',
    address: '43511 Bluebird Trail, Punta Gorda, FL 33982',
    summary: 'Standing weekday session at Cypress Lodge — a soft alternative to yoga.',
    details: [
      'Most weekdays at 10:30 AM at Cypress Lodge.',
      'Confirm the current month’s days and fee on Rec1 or the town calendar before you go.',
    ],
    href: REC1_CATALOG_URL,
    sourceNote: 'Babcock Ranch Parks & Rec calendar, September 2026 (babcockranchliving.com).',
  },
  {
    id: 'sound-bath',
    interestId: 'sound-bath',
    name: 'Sound Bath',
    intensity: 'gentle',
    intensityLabel: 'Soft alternative',
    when: 'Sun 9/20 · 6:00 PM',
    venue: 'Lake Timber Lodge',
    address: '42147 Lake Timber Dr, Punta Gorda, FL 33982',
    summary: 'Evening sound bath at Lake Timber Lodge — rest, not a flow class.',
    details: ['Sunday, September 20, 2026, 6:00 PM at Lake Timber Lodge.'],
    href: RANCH_CALENDAR_URL,
    sourceNote: 'Babcock Ranch Parks & Rec calendar, September 2026 (babcockranchliving.com).',
  },
  {
    id: 'power-yoga',
    interestId: 'power-yoga',
    name: 'Power Yoga',
    intensity: 'active',
    intensityLabel: 'Not gentle — Baptiste flow',
    when: 'Tue evenings · e.g. 9/15 & 9/22 · 6:30 PM',
    venue: 'Cypress Lodge',
    address: '43511 Bluebird Trail, Punta Gorda, FL 33982',
    summary:
      'Baptiste-style power flow. Listed so it is not mistaken for a gentle class — not a Lisa default.',
    details: [
      'Tuesday evenings, including September 15 and 22, 2026, 6:30 PM at Cypress Lodge.',
      'This is an active heated-style flow, not restorative or yin.',
    ],
    href: REC1_CATALOG_URL,
    sourceNote: 'Babcock Ranch Parks & Rec calendar, September 2026 (babcockranchliving.com).',
  },
  {
    id: 'wellway-yoga',
    interestId: 'wellway-yoga',
    name: 'WellWay yoga',
    intensity: 'studio',
    intensityLabel: 'Confirm on the schedule',
    when: 'Among 40+ weekly classes',
    venue: 'WellWay Babcock Ranch',
    address: '42880 Crescent Loop, Suite 100, Punta Gorda, FL 33982',
    summary:
      'Yoga is on the WellWay group-class slate. Call or check the live schedule for a gentle option — there is no published class named Gentle Yoga.',
    details: [
      'WellWay lists 40+ weekly group classes, including yoga. Intensity varies by class.',
      'Do not assume a named Gentle Yoga offering. Confirm the current slate at the club or by phone.',
    ],
    href: 'https://wellway.com/wellness-club-locations/babcock-ranch-florida/',
    phone: '2393967430',
    sourceNote:
      'WellWay Babcock Ranch class offering (wellway.com). Phone (239) 396-7430. No named Gentle Yoga class is published — we do not invent one.',
  },
];

export function sessionByInterestId(id: string): MovementSession | undefined {
  return YOGA_SESSIONS.find((s) => s.interestId === id);
}

export function sessionsForInterestIds(ids: string[]): MovementSession[] {
  const want = new Set(ids);
  return YOGA_SESSIONS.filter((s) => want.has(s.interestId));
}

export const GENTLE_SESSIONS = YOGA_SESSIONS.filter((s) => s.intensity === 'gentle');
